<template>
    <div class="cart-item-comment-editor" v-if="hasComment || isEditAllowed">
        <div class="current-comment" v-if="!isEditing">
            <p
                v-if="hasComment"
                :class="{ 'operator-comment': props.isOperatorUser, 'editable-comment': isEditAllowed }"
                @click="editComment"
            >
                {{ item.comment }}
            </p>
            <span
                v-if="!hasComment && isEditAllowed"
                class="edit-comment-btn comment-btn"
                :class="{ disabled: areButtonsDisabled }"
                @click="editComment"
            >
                {{ translations.cart.addComment }}
            </span>
        </div>

        <div class="comment-editor" v-if="isEditing">
            <Textarea
                ref="textareaComponentRef"
                class="comment-textarea"
                v-model="commentEditedValue"
                :autoResize="true"
                :maxLength="60"
                @keydown.enter.prevent="saveComment"
            />

            <div class="action-buttons">
                <span
                    class="cancel-comment-btn comment-btn"
                    :class="{ disabled: areButtonsDisabled }"
                    @click="cancelEdit"
                >
                    {{ translations.general.cancel }}
                </span>
                <span
                    class="save-comment-btn comment-btn"
                    :class="{ disabled: areButtonsDisabled }"
                    @click="saveComment"
                >
                    {{ translations.general.save }}
                </span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { ICartItem } from "@/data/model/Customer";
    import { useCartStore } from "@/data/stores/cart";
    import { translations } from "@/shared/localization/translations";
    import { computed, nextTick, ref, watch } from "vue";

    import Textarea from "primevue/textarea";

    // define properties
    const props = defineProps<{
        item: ICartItem;
        disabled: boolean;
        isOperatorUser: boolean;
        areItemCommentsAllowed: boolean;
    }>();

    const cart = useCartStore();

    const commentEditedValue = ref("");
    const isEditing = ref(false);
    const textareaComponentRef = ref<any>(null);
    const isProcessing = ref(false);
    const areButtonsDisabled = computed(() => props.disabled || isProcessing.value);

    const hasComment = computed(() => props.item.comment != null && props.item.comment.trim() !== "");
    const isEditAllowed = computed(() => props.areItemCommentsAllowed && props.item.canHaveComments);

    function editComment() {
        if (areButtonsDisabled.value || !isEditAllowed.value) {
            return;
        }
        commentEditedValue.value = props.item.comment ?? "";
        isEditing.value = true;
    }

    async function saveComment() {
        if (areButtonsDisabled.value) {
            return;
        }

        isProcessing.value = true;
        const comment = commentEditedValue.value.trim();
        if (comment === "") {
            await cart.removeCartItemComment(props.item);
        } else {
            await cart.setCartItemComment(props.item, comment);
        }
        isProcessing.value = false;

        isEditing.value = false;
        commentEditedValue.value = "";
    }

    function cancelEdit() {
        if (areButtonsDisabled.value) {
            return;
        }

        isEditing.value = false;
        commentEditedValue.value = "";
    }

    watch(isEditing, async (newValue) => {
        if (newValue) {
            await nextTick();
            textareaComponentRef.value?.$el?.focus();

            textareaComponentRef.value?.$el?.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        }
    });

    watch(commentEditedValue, (newValue) => {
        if (newValue?.length > 60) {
            newValue = newValue.substring(0, 60);
        }
        commentEditedValue.value = newValue ?? "";
    });
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .cart-item-comment-editor {
        font-size: 14px;
        padding-top: 0.5em;
        margin-bottom: -12px;

        .comment-btn {
            display: inline-block;
            cursor: pointer;
            transition: all 0.2s variables.$easeOutExpo;
            padding: 0.5em;
            margin: -0.5em 0;
            margin-right: 0.25em;

            @include mixins.hover-and-active {
                text-decoration: underline;
                text-underline-offset: 0.25em;
            }

            &.disabled {
                cursor: default !important;
                text-decoration: none !important;
            }
        }

        .current-comment {
            position: relative;

            p {
                font-size: 0.875em;
                font-style: italic;
                word-break: break-word;
                margin: 0;

                // end user comment - default
                color: colors.$tastierMinorDarkGrey;
                font-weight: normal;

                &.operator-comment {
                    color: colors.$red;
                    font-weight: bold;
                }

                &.editable-comment {
                    cursor: pointer;
                }
            }

            span {
                font-size: 0.875em;
                position: relative;
                left: -0.5em;

                &.edit-comment-btn {
                    color: colors.$green;
                    color: var(--colorPrimary);

                    @include mixins.hover-and-active {
                        color: colors.$greenHover;
                        color: var(--colorPrimaryHover);
                    }
                }
            }
        }

        .comment-editor {
            position: relative;

            .action-buttons {
                position: absolute;
                bottom: 0.75em;
                right: 1em;

                span {
                    font-size: 0.875em;

                    &.save-comment-btn {
                        color: colors.$tastierGreen;
                        color: var(--colorPrimary);

                        @include mixins.hover-and-active {
                            color: colors.$greenHover;
                            color: var(--colorPrimaryHover);
                        }
                    }

                    &.cancel-comment-btn {
                        color: colors.$tastierOrange;

                        @include mixins.hover-and-active {
                            color: colors.$orangeHover;
                        }
                    }
                }
            }
        }

        .comment-textarea.p-inputtextarea {
            width: 100%;
            resize: none;
            padding-bottom: 2em;
            font-size: 1em;

            &:focus {
                box-shadow: none;
                border-color: var(--colorPrimary);
            }

            &:enabled:hover {
                border-color: var(--colorPrimary);
            }
        }
    }
</style>
